import { LoadingAnimation, MultiStepNavigation, toastNotifications, useAppForm, Modal } from '@uag/react-core';
import { useAssignCompanySigner, useGetRequestById } from '@uag/welcome-shared/src/api/queries/requests/requests';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { create, InstanceProps } from 'react-modal-promise';
import { useParams } from 'react-router';
import { z } from 'zod';

import { i18n } from 'i18n';
import { SelectSigner, SignerType } from './components/SelectSigner';
import { SignContractForm } from './components/SignContractForm';

type Props = InstanceProps<SignerType>;

const signContractSchema = z.object({
    iAgreeTheTermsAndConditions: z.boolean().refine((value) => value, {
        message: i18n.t('isRequired', { label: i18n.t('field') }),
    }),
    iHaveReadAndUnderstood: z.boolean().refine((value) => value, {
        message: i18n.t('isRequired', { label: i18n.t('field') }),
    }),
});

export type FormType = z.infer<typeof signContractSchema>;

export const ChooseSignerModal = ({ isOpen, onResolve }: Props) => {
    const [currentStep, setCurrentStep] = useState(0);

    const { t } = useTranslation();
    const { requestId = '' } = useParams();

    const [signerType, setSignerType] = useState<SignerType>('Self');
    const [signerEmail, setSignerEmail] = useState<string | undefined>();
    const [contractLanguage, setContractLanguage] = useState<string>('');

    const { mutateAsync: assignCompanySigner, isPending } = useAssignCompanySigner();
    const { data: apiRequest, isLoading: isRequestLoading } = useGetRequestById(requestId);

    const isContinueDisabled = !contractLanguage || (signerType === 'SomeoneElse' && !signerEmail);
    const request = apiRequest?.data;

    const form = useAppForm({
        onSubmit: async () => {
            try {
                await assignCompanySigner({
                    id: requestId,
                    params: {
                        signerEmail: signerType === 'SomeoneElse' ? signerEmail : undefined,
                        twoLetterLanguage: contractLanguage,
                    },
                });
            } catch (error) {
                toastNotifications.errorDetail(
                    {
                        title: t('failed'),
                    },
                    error,
                );

                return;
            }

            onResolve(signerType);
        },
        defaultValues: {
            iAgreeTheTermsAndConditions: false,
            iHaveReadAndUnderstood: false,
        },
        validators: {
            onSubmit: signContractSchema,
        },
    });

    const submitText = signerType === 'SomeoneElse' ? t('sendToSignatory') : t('signNow');
    const handleClose = () => onResolve(undefined);

    return (
        <Modal opened={isOpen} title={t('activateMyDoka')} onClose={handleClose}>
            <div className="gap-2xl flex flex-col">
                {currentStep === 0 && (
                    <div>
                        {isRequestLoading && <LoadingAnimation />}
                        <SelectSigner
                            selectedLanguage={contractLanguage}
                            signerEmail={signerEmail}
                            signerType={signerType}
                            onLanguageChange={(language) => setContractLanguage(language ?? '')}
                            onSignerEmailChange={setSignerEmail}
                            onSignerTypeChange={setSignerType}
                        />
                    </div>
                )}
                {currentStep === 1 && (
                    <div className="w-148">
                        {request ? <SignContractForm form={form} request={request} /> : <LoadingAnimation />}
                    </div>
                )}
                <MultiStepNavigation
                    backBtnText={t('back')}
                    canNavToStep={(step) => step === 0 || (step === 1 && !isContinueDisabled)}
                    currentStep={currentStep}
                    forwardBtnText={t('continue')}
                    isLoading={isPending}
                    navToStep={setCurrentStep}
                    submitText={submitText}
                    totalSteps={2}
                    onPrimaryAction={form.handleSubmit}
                />
            </div>
        </Modal>
    );
};
export const openChooseSignerModal = create(ChooseSignerModal);
